import React, { useMemo, useState } from 'react';

/**
 * Tabel modul x aksi yang dapat dipakai ulang di dua konteks:
 * - Role form: `selectedIds` adalah Set permissionId yang dicentang langsung.
 * - Employee access: `selectedIds` adalah efek gabungan role + override,
 *   dan `sourceById` (opsional) menandai baris yang berasal dari role
 *   ("dari role") vs override eksplisit, lewat badge berwarna berbeda.
 *
 * `catalog` adalah array modul dari GET /office/permissions:
 *   [{ module, moduleLabel, icon, programCode, permissions: [{ id, code, action, label }] }]
 */
export default function PermissionMatrix({
  catalog,
  selectedIds,
  sourceById = {},
  onToggle,
  readOnly = false,
}) {
  // Filter per domain (CLP/HOL/SMS/...) -- bukan cuma quick-jump, tapi
  // benar-benar menyaring baris yang tampil, supaya admin tidak perlu
  // scroll di antara puluhan modul lintas domain untuk cari 1-2 yang
  // relevan. Default "Semua" (tidak difilter).
  const [activeDomain, setActiveDomain] = useState('Semua');

  const domains = useMemo(
    () => [...new Set((catalog || []).map((mod) => mod.programCode || '—'))],
    [catalog],
  );

  const visibleCatalog = useMemo(() => {
    if (activeDomain === 'Semua') return catalog || [];
    return (catalog || []).filter((mod) => (mod.programCode || '—') === activeDomain);
  }, [catalog, activeDomain]);

  if (!catalog?.length) {
    return <p className="text-muted small mb-0">Katalog permission kosong.</p>;
  }

  return (
    <div>
      {/* Filter domain -- badge bertindak seperti tab, bukan cuma jump-link */}
      <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
        <span className="text-muted small fw-medium me-1">Domain:</span>
        <button
          type="button"
          onClick={() => setActiveDomain('Semua')}
          className={`btn btn-sm rounded-pill px-3 ${activeDomain === 'Semua' ? 'btn-dark' : 'btn-outline-secondary'}`}
        >
          Semua ({catalog.length})
        </button>
        {domains.map((domain) => {
          const count = catalog.filter((mod) => (mod.programCode || '—') === domain).length;
          return (
            <button
              key={domain}
              type="button"
              onClick={() => setActiveDomain(domain)}
              className={`btn btn-sm rounded-pill px-3 ${activeDomain === domain ? 'btn-dark' : 'btn-outline-secondary'}`}
            >
              {domain} ({count})
            </button>
          );
        })}
      </div>

      <div className="table-responsive rounded-3 border">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr className="text-muted small text-uppercase">
              <th className="py-3 ps-4 border-0" style={{ width: '30%' }}>
                Modul
              </th>
              <th className="py-3 border-0">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {visibleCatalog.map((mod) => (
              <tr key={`${mod.programCode}:${mod.module}`}>
                <td className="ps-4 py-3">
                  <div className="d-flex align-items-center">
                    {mod.icon && <i className={`bi ${mod.icon} text-primary-emphasis me-2`}></i>}
                    <span className="fw-medium">{mod.moduleLabel}</span>
                    {mod.programCode && (
                      <span className="badge bg-light text-secondary border ms-2" style={{ fontSize: '0.65rem' }}>
                        {mod.programCode}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3">
                  <div className="d-flex flex-wrap gap-2">
                    {mod.permissions.map((perm) => {
                      const checked = selectedIds.has(perm.id);
                      const source = sourceById[perm.id];
                      let badgeClass = 'btn-outline-secondary';
                      let title = perm.label;
                      if (source === 'deny') {
                        badgeClass = 'btn-danger';
                        title = 'Ditolak lewat override (menang atas role)';
                      } else if (checked && source === 'division') {
                        badgeClass = 'btn-info text-white';
                        title = 'View otomatis dari Manager Divisi';
                      } else if (checked && source === 'role') {
                        badgeClass = 'btn-primary';
                        title = 'Diwarisi dari role';
                      } else if (checked) {
                        badgeClass = 'btn-success';
                        title = 'Diberikan lewat override';
                      }

                      return (
                        <button
                          key={perm.id}
                          type="button"
                          disabled={readOnly}
                          title={title}
                          className={`btn btn-sm rounded-pill ${badgeClass}`}
                          onClick={() => onToggle?.(perm, source)}
                        >
                          {checked && source === 'division' && <i className="bi bi-eye-fill me-1"></i>}
                          {checked && source !== 'division' && <i className="bi bi-check-lg me-1"></i>}
                          {source === 'deny' && <i className="bi bi-slash-circle me-1"></i>}
                          {perm.action}
                          {source === 'role' && <i className="bi bi-link-45deg ms-1"></i>}
                          {source === 'division' && <i className="bi bi-diagram-3-fill ms-1"></i>}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
